import styled from 'styled-components/macro';
import {createContext, useContext, useState} from "react";
import classNames from "classnames";
import Skeleton from "react-loading-skeleton";
import {useToast} from "@chakra-ui/react";
import {useActions, useLoggedIn, useModal, useRelativeTime, useRelativeTimeFormatter, useSettings} from "../store";
import {useFeedback} from "../store/feedback";
import {useBlock} from "../api/user";
import {useBaseCommunity, useStoreChildComments, useUsername} from "../store/hooks";
import {useDelete} from "../api/delete";
import {useCommentVote} from "../api/comment";
import SaveItem from "./save_item";
import ReportItem from "./report_item";
import NavLink from "./nav_link";
import {CommentVote} from "./vote";
import {EditComment} from "./edit";
import Tooltip from "./tooltip";
import ModActions from "./mod_actions";
import ModTools, {Reports} from "./mod_tools";
import RoleIndicator from "./role_indicator";
import TextContent from "./text_content";
import CreateComment from "./create_comment";
import {TextButton} from "./button";
import ConfirmButton from "./confirm_button";
import Signup from "./signup";
import GiveAward from "./subscriptions/give_award";
import useProfilePicture from "../utils/profile_picture";
import ProfilePicture from "./profile_picture";
import {themecolors} from "../utils/themecolors";
import {Check, Award as LucideAward, Handshake, Eraser, Pin, Hand} from "lucide-react";

const MAX_DEPTH = 5;
const MAX_CHILDREN = 5;

const Main = styled.div``;
const Info = styled.span``;
const Actions = styled.div``;
const Author = styled.span``;
const Deleted = styled.span``;
const Score = styled.span``;
const Age = styled.span``;
const Parent = styled.div``;
const ParentTitle = styled.span``;
const Edited = styled.span``;
const ParentAuthor = styled.span``;
const ParentCommunity = styled.span``;
const Approved = styled.span``;
const Collapse = styled.a``;
const Child = styled.div``;
const Reply = styled.div``;
const SkeletonizedContent = styled.div``;
const More = styled.div``;
const NewUser = styled.span``;
const Flair = styled.span``;
const Stickied = styled.span``;
const Award = styled.div``;

export const PostContext = createContext(null);

export const Comment = styled(({className, comment, showCommunity, contextual, depth, hideComments, postAuthor, markRead, markUnread, blocked}) => {
    const {theme} = useSettings();
    const toast = useToast();
    const actions = useActions();
    const date = new Date(comment?.created);
    const ago = useRelativeTime(comment?.created, true);
    const modal = useModal();
    const loggedIn = useLoggedIn();
    const formatRelativeTime = useRelativeTimeFormatter();
    const username = useUsername();
    const {feedbackCh} = useFeedback();
    const vote = useCommentVote(feedbackCh);
    const [edit, setEdit] = useState(false);
    const [reply, setReply] = useState(false);
    const [reports, setReports] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const postContext = useContext(PostContext);
    const {comments} = useStoreChildComments(comment?.id);
    const block = useBlock(feedbackCh);
    const _delete = useDelete(feedbackCh);
    const baseCommunity = useBaseCommunity();

    const postLink = postContext?.link || `/c/${comment?.community}/p/${comment?.post_uuid}/x/c`;
    const uuid = comment?.uuid;
    const handleVote = async (dir) => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                await vote(comment, dir);
            } catch {}
        }
    };
    const handleBlock = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                await block(comment.author);
            } catch {}
        }
    };
    const handleReport = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                modal.open(ReportItem, {comment});
            } catch {}
        }
    };
    const handleSave = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                modal.open(SaveItem, {comment});
            } catch {}
        }
    };
    const handleDelete = async () => {
        modal.close();
        const action = actions.commentDeleted(comment.id);
        try {
            await _delete(comment.id, 'comment');
        } catch {
            action.undo?.();
        }
    };
    const handleEdit = () => {
        setEdit(!edit);
    };
    const handleReply = () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            setReply(!reply);
        }
    };
    const handleAward = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(GiveAward, {id: comment.id, type: 'comment'});
        }
    };
    if (comment?.is_deleted && comments.length === 0) {
        return false;
    }

    const scrollToElement = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({
                block: 'start',
                inline: 'nearest',
            });
            //window.scrollBy(0, 20);
        }
    };

    const parentId = comment?.comment_parent_id;

    const trimDepth = comments && comments.length > 0 && typeof depth === 'number' && depth >= MAX_DEPTH;
    const trimChildren = !trimDepth && comments && postContext?.commentMap && !postContext.commentMap.get(comment?.id);
    const visibleComments = trimDepth ? [] : (trimChildren ? comments?.slice(0, MAX_CHILDREN) : comments);
    const hiddenComments =
        (trimDepth ? comments : (trimChildren ? comments.slice(MAX_CHILDREN) : []))
            .reduce((a, x) => a + 1 + x.size, 0) || 0;

    return (
        <>
            <div
                id={`comment-${comment?.id}`}
                className={classNames(
                    className,
                    'comment',
                    {
                        minimized: collapsed,
                        removed: comment?.moderation && comment.moderation.removed_at !== 0,
                    }
                )}
            >
                {!comment?.is_deleted && <CommentVote className='vote' vote={comment?.vote_state} onVote={handleVote}/>}
                <Main className='body'>
                    {!contextual && <Collapse className={"collapse"}
                        onClick={(event) => {
                            event.preventDefault();
                            setCollapsed(!collapsed);
                        }}
                    />
                   }
                    {comment?.post_uuid && (
                        <Parent>
                            <ParentTitle>
                                <NavLink href={postLink} react_route>
                                    {comment.post_title ? comment.post_title : 'deleted'}
                                </NavLink>
                            </ParentTitle>
                            {showCommunity && <ParentCommunity>in <NavLink href={`/c/${comment.community}/`} react_route>{comment.community}</NavLink></ParentCommunity>}
                        </Parent>
                    )}
                    <Info style={{marginBottom: baseCommunity === "TheDonald" ? '2px' : '4px'}}>
                        {!comment && <Skeleton width={200} height={12} />}
                        {comment && !comment.is_deleted && <>

                                {baseCommunity !== "TheDonald" &&
                                    <div style={{marginRight: '6px'}}>
                                    <ProfilePicture username={comment?.author} profilePicture={useProfilePicture(comment?.author, comment?.profile_picture, theme)}
                                                    size={18} fontSize={11}/>
                                    </div>
                                }
                                <Author>
                                    <NavLink href={`/u/${comment.author}/`} react_route>{comment.author}</NavLink>
                                </Author>
                                {comment?.author_flair_class && <>
                                    <Flair className='user-flair' data-flair={comment.author_flair_class}>
                                        {comment.author_flair_text}
                                    </Flair>
                                    {' '}
                                </>}
                                <RoleIndicator
                                    mod={comment.is_moderator}
                                    admin={comment.is_admin}
                                    op={postAuthor && comment.author === postAuthor}
                                    proTier={comment.pro_tier}
                                />
                            </>}
                        {comment && comment.is_deleted && <Deleted>{`deleted`}</Deleted>}
                        {comment && (
                            <Tooltip content={`+${comment?.score_up} / -${comment?.score_down}`}>
                                <Score>{comment?.score} points</Score>
                            </Tooltip>
                        )}
                        <Tooltip content={date.toLocaleString()}>
                            <span>{comment && <Age>{ago} ago</Age>}</span>
                        </Tooltip>
                        {comment?.awards > 0 &&
                            <Award>
                                <LucideAward size={16} color={"#dcba00"} />
                            </Award>
                        }
                        {comment?.is_new_user && (
                            <Tooltip content={`This user is either new, or hasn't posted much.`}>
                                <NewUser>
                                    <Hand size={14} />
                                </NewUser>
                            </Tooltip>
                        )}
                        {comment?.is_edited && <Edited><Eraser size={14} /></Edited>}
                        {comment?.moderation && comment.moderation.approved_at !== 0 &&
                        <Tooltip content={`Approved ${formatRelativeTime(comment.moderation.approved_at)} ago by ${comment.moderation.approved_by}`}>
                            <Approved>
                                <Check size={16} />
                            </Approved>
                        </Tooltip>}
                        {comment?.is_stickied && <Stickied><Pin size={14} color={"#ffbe00"} fill={"#ffbe00"} /></Stickied>}
                        {comment?.moderation && <ModTools comment={comment} />}
                    </Info>
                    {!comment && <SkeletonizedContent><Skeleton width={'100%'} height={12} count={3} /></SkeletonizedContent>}
                    {comment && !comment.is_deleted && <TextContent removed={comment.is_removed} deleted={comment.is_deleted} content={comment.content} type={"Comment"} removalSource={comment?.removal_source} />}
                    {(!comment || !comment.is_deleted) && (
                        <Actions>
                            <NavLink href={`${postLink}/${comment?.uuid}`} react_route>permalink</NavLink>
                            {!contextual && parentId !== 0 &&
                                <TextButton onClick={() => scrollToElement(`comment-${parentId}`)}>parent</TextButton>
                            }
                            {contextual && (<>
                                <NavLink href={`${postLink}/${comment?.uuid}?d=50`} react_route>context</NavLink>
                                <NavLink href={postLink} react_route>all comments ({comment?.post_comments})</NavLink>
                            </>)}
                            <TextButton onClick={handleAward}>
                                    award
                            </TextButton>
                            {loggedIn &&
                                <TextButton onClick={handleSave}>save</TextButton>
                            }
                            <TextButton onClick={handleReport} className={'action-report'}>report</TextButton>
                            {username !== comment?.author &&
                                <ConfirmButton onClick={handleBlock}>block</ConfirmButton>
                            }
                            {username === comment?.author && (<>
                                <TextButton onClick={handleEdit}>edit</TextButton>
                                <ConfirmButton onClick={handleDelete}>delete</ConfirmButton>
                            </>)}
                            {comment && comment.moderation && (
                                <ModActions comment={comment} setReports={setReports} reports={reports}/>
                            )}
                            {markRead && <TextButton onClick={markRead}>mark read</TextButton>}
                            {markUnread && <TextButton onClick={markUnread}>mark unread</TextButton>}
                            <Reply>
                                <TextButton onClick={handleReply}>reply</TextButton>
                            </Reply>
                        </Actions>
                    )}
                    {reports && <Reports id={comment?.id} type={'comment'}/>}
                    {edit && <EditComment comment={comment} onClose={() => setEdit(false)}/>}
                    {reply && (
                        <CreateComment
                            comment={comment}
                            onCreated={() => {
                                if (!postContext) {
                                    toast({
                                        title: 'Your comment has been posted!',
                                        status: 'success',
                                        isClosable: true,
                                    });
                                }
                            }}
                            onClose={() => setReply(false)}
                        />
                    )}
                </Main>

                {!hideComments && visibleComments?.map((comment) =>
                    !blocked?.includes(comment?.author) && comment?.is_shown && (
                        <Child key={comment.id} className='child'>
                            <Comment comment={comment} parentUuid={uuid} depth={depth + 1} postAuthor={postAuthor} blocked={blocked}/>
                        </Child>
                    )
                )}
                {!collapsed && trimChildren && hiddenComments > 0 && (
                    <More>
                        <TextButton onClick={() => postContext.expandComment(comment.id)}>
                            load {hiddenComments} more comments...
                        </TextButton>
                    </More>
                )}
                {!collapsed && trimDepth && hiddenComments > 0 && (
                    <More>
                        <NavLink href={`${postLink}/${comment?.uuid}`} react_route>
                            view {hiddenComments} more comments...
                        </NavLink>
                    </More>
                )}
            </div>
            </>
    )})`
  display: flex;

  padding: 12px 12px 12px 0;
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  ${({contextual, theme}) => !contextual && `
      flex: 1 1 100%;
      margin: 0 0 12px 0px;
      padding: 6px 0 0 10px;
      flex-wrap: wrap;
      position: relative;
  `}
 
  & ${Child} & {
    margin: 0 0 0 2px;
  }
  ${Child}, ${More} {
    flex-basis: 100%;
    margin: 8px 0 0 4px;
  }
  &.minimized {
    .vote {
      visibility: hidden;
      height: 0;
    }
    .child, ${TextContent}, ${Actions} {
      display: none;
    }
  }
  
  ${Collapse} {
    cursor: pointer;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.light_input};
    width: 7px;
    transition: background-color .3s,color .3s;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    -webkit-tap-highlight-color: transparent;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : themecolors.light_medium_impact_text};
    &:hover {
      color: #fff;
      background-color: #9f9f9f;
    }
  }

  ${Main} {
    flex: 1 0;
    margin-right: 5px;
    overflow: hidden;
  }
  
  ${Info} {
    font-size: 12px;
    flex-wrap: wrap;
    display: flex;
    align-items: center;
    white-space: break-spaces;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  }

  ${Author},
  ${ParentAuthor} a,
  ${ParentCommunity} a {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
    font-weight: 600;
  }
  ${Deleted} {
    margin-left: 15px;
    color: #818181;
  }

  ${Author}, ${Score}, ${Age}, ${Deleted} {
    margin-right: 6px;
  }
  
  ${TextContent}, ${SkeletonizedContent} {
    font-size: 14px;
  }
  ${SkeletonizedContent} {
    margin: 6px 0;
  }
  ${Actions} {
    display: flex;
    flex-wrap: wrap;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_zero_impact_text};
    font-size: 13px;
    padding-top: 2px;
  }
  ${Actions} > * {
    padding: 0;
    margin-right: 8px;
  }
  ${Parent} {
    margin-bottom: 4px;
  }
  ${ParentTitle} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_1};
    font-size: 14px;
    font-weight: ${({theme}) => theme.color.name === 'dark' ? 500 : 400};
  }
  ${EditComment} {
    margin-bottom: 12px;
  }
  ${Reports} {
    margin-bottom: 12px;
  }
  ${Edited}, ${NewUser} {
    margin-left: 6px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : themecolors.light_low_impact_text};
  }
  ${Award} {
    margin-left: 6px;
  }
    
  ${ParentAuthor}, ${ParentCommunity} {
    font-size: 13px;
    margin-left: 4px;
  }
  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }
  .theme-dark & {
    color: ${({theme}) => themecolors.pure_white};
  }
  ${Approved} {
    color:  ${({theme}) => theme.placeholder_green_1};
    margin: 0 4px;
    font-size: 13px;
  }
  ${Reply} {
    font-weight: 600;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
  }
  ${More} {
    font-size: 13px;
    font-weight: 600;
    color:  ${({theme}) => theme.placeholder_link_2};
    margin-left: 24px;
    padding-bottom: 4px;
  }
  ${Stickied} {
    color: #ffbe00;
  }
`;

export default Comment;
